/**
 * PersonCell — one person, as a face and a name.
 *
 * The "Created by" / "Owner" / "Assignee" cell in a table row. It was written
 * out longhand at every call site — a `ProfileImage` with the picture read
 * from exactly one key, `initialsOf(name)` beside it, `flex items-center
 * gap-2 min-w-0` around it — and the copies had drifted: some read
 * `profile_picture`, some `ProfilePicture`, some passed no email so the
 * fallback colour changed between two tables showing the same person.
 *
 * The row goes through `accessModel.normalise`, the same function
 * `CollaboratorStack` uses, so a face here and a face in the access stack are
 * resolved by identical rules. Anything with a name works; nothing needs a
 * particular spelling.
 *
 * A missing person renders an em-dash, NOT a silhouette. A grey avatar reads
 * as "somebody, unphotographed"; the dash reads as "the backend did not say",
 * which is the truth when a payload carries no creator at all.
 */
import React from 'react';
import ProfileImage from '../../Header/ProfileImage';
import { normalise } from './accessModel';

export interface PersonCellProps {
  /** A collaborator/owner row in any backend shape. Falsy → the em-dash. */
  person: any;
  /** Matches the avatar sizes elsewhere in a table row. */
  size?: 'sm' | 'xs';
  /** Faces only — for a narrow column where the name would just truncate. */
  hideName?: boolean;
  /**
   * A quiet second line under the name — a created/updated stamp, a role.
   *
   * "Created by" is a claim about a person AND a moment, and the two were
   * split across two columns with a stack of unrelated cells between them.
   * Kept as a node rather than a date so the caller owns its own formatting;
   * ignored when `hideName` is set, since there is nothing to sit under.
   */
  meta?: React.ReactNode;
  className?: string;
}

const PersonCell: React.FC<PersonCellProps> = ({
  person,
  size = 'sm',
  hideName = false,
  meta,
  className,
}) => {
  const resolved = person ? normalise(person) : null;

  if (!resolved || !resolved.name) {
    return <span className="text-xs text-fg-muted">—</span>;
  }

  const showMeta = !hideName && !!meta;

  return (
    <div
      className={`flex min-w-0 items-center gap-2 ${className || ''}`}
      title={resolved.email ? `${resolved.name} · ${resolved.email}` : resolved.name}
    >
      <ProfileImage
        profilePicture={resolved.avatar}
        email={resolved.email || null}
        initials={resolved.initials}
        name={resolved.name}
        size={size === 'xs' ? 'w-5 h-5' : 'w-6 h-6'}
      />
      {!hideName && (
        <div className="min-w-0">
          <div className="truncate text-sm text-fg-body">{resolved.name}</div>
          {showMeta && (
            <div className="truncate text-[11px] text-fg-muted">{meta}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default PersonCell;
